/**
 * Side-by-side code diff modal for the MOSS panel of the Layer 10
 * Evidence Dashboard.
 *
 * Fetches GET /api/cheating/code-diff?test_id=...&peer_test_id=...&question_idx=...
 * and renders both candidate's code in two scrollable columns. No fancy
 * syntax highlighting yet — that's a polish task. Keeping the columns
 * scroll-linked so the recruiter can scan vertically without losing alignment.
 *
 * When the backend returns null for either side (pre-Phase-8 row, no
 * code_text stored), we render a friendly empty state on that column
 * instead of breaking the layout.
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { API_BASE_URL } from '@/config/api';

export default function MossDiffModal({
  testId, questionIdx, peerTestId,
  candidateLabel = 'Candidate',
  peerLabel = 'Peer',
  onClose,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Synced scroll between the two code panes — when one scrolls, the
  // other follows. Helps eyeball matching regions quickly.
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const syncingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({
          test_id: testId,
          peer_test_id: peerTestId,
          question_idx: String(questionIdx),
        });
        const r = await fetch(`${API_BASE_URL}/api/cheating/code-diff?${qs.toString()}`, {
          credentials: 'include',
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.error || `HTTP ${r.status}`);
        }
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load diff');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [testId, peerTestId, questionIdx]);

  // Mirror scroll on one column to the other.
  const onScroll = (src, dst) => () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (dst.current) {
      dst.current.scrollTop = src.current.scrollTop;
    }
    // Release the flag on next frame so the mirrored scroll doesn't
    // re-trigger us infinitely.
    requestAnimationFrame(() => { syncingRef.current = false; });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900">
              Side-by-side code diff — Question {questionIdx + 1}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {candidateLabel} vs {peerLabel}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            Loading code…
          </div>
        )}

        {error && (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-700 gap-2">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
            <div>{error}</div>
          </div>
        )}

        {!loading && !error && data && (
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 overflow-hidden">
            <CodePane
              label={candidateLabel}
              testId={testId}
              row={data.candidate}
              scrollRef={leftRef}
              onScroll={onScroll(leftRef, rightRef)}
              side="left"
            />
            <CodePane
              label={peerLabel}
              testId={peerTestId}
              row={data.peer}
              scrollRef={rightRef}
              onScroll={onScroll(rightRef, leftRef)}
              side="right"
            />
          </div>
        )}

        {!loading && !error && data && (data.note ? (
          <div className="px-4 py-2 border-t border-slate-100 text-xs text-slate-500">
            {data.note}
          </div>
        ) : null)}
      </div>
    </div>
  );
}

function CodePane({ label, testId, row, scrollRef, onScroll, side }) {
  // Border-l on the right pane to visually separate the two columns.
  const borderCls = side === 'right' ? 'md:border-l border-slate-200' : '';
  return (
    <div className={`flex flex-col min-h-0 ${borderCls}`}>
      <div className="px-4 py-2 border-b border-slate-100 bg-slate-50">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        <div className="text-xs text-slate-500 truncate">
          <code>{testId}</code>
          {row?.candidate_email && <> · {row.candidate_email}</>}
        </div>
        {row && (
          <div className="text-[11px] text-slate-400 mt-0.5">
            {row.language || 'unknown'} · {row.code_length ?? 0} chars · {row.token_count ?? 0} tokens
          </div>
        )}
      </div>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 overflow-auto bg-slate-900 text-slate-100"
      >
        {row?.code_text ? (
          <pre className="text-xs p-3 leading-5 whitespace-pre">{row.code_text}</pre>
        ) : (
          <div className="p-6 text-sm text-slate-400">
            No code stored for this submission. (Pre-Phase-8 submission — raw code
            wasn't persisted before the storage column was added.)
          </div>
        )}
      </div>
    </div>
  );
}
